import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectsAPI } from '../../services/api';
import { useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import AiWritingAssistant from '../../components/Projects/AiWritingAssistant';
import AiFieldAssistant from '../../components/Projects/AiFieldAssistant';

const CreateProject = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    stage: '',
    fundingNeeded: '',
    currency: 'EUR',
    country: '',
    businessModel: '',
    targetMarket: '',
    competitiveAdvantage: '',
    teamSize: '',
    expectedROI: '',
    timeline: '',
    contactInfo: ''
  });

  const categories = [
    'Technologie',
    'Santé',
    'Éducation',
    'Finance',
    'E-commerce',
    'Environnement',
    'Transport',
    'Immobilier',
    'Agriculture',
    'Tourisme',
    'Autre'
  ];

  const stages = [
    'Idée',
    'Prototype',
    'MVP (Produit Minimum Viable)',
    'Beta',
    'Lancement',
    'Croissance',
    'Expansion'
  ];

  const currencies = [
    { code: 'EUR', name: 'Euro (€)' },
    { code: 'USD', name: 'Dollar US ($)' },
    { code: 'GBP', name: 'Livre Sterling (£)' },
    { code: 'CHF', name: 'Franc Suisse (CHF)' },
    { code: 'CAD', name: 'Dollar Canadien (CAD)' },
    { code: 'XOF', name: 'Franc CFA (XOF)' },
    { code: 'MAD', name: 'Dirham Marocain (MAD)' },
    { code: 'TND', name: 'Dinar Tunisien (TND)' }
  ];

  useEffect(() => {
    // Liste de pays africains (focus francophone) + pays majeurs
    // Fix: API /countries ne fonctionne pas, utilisation d'une liste statique
    const africanCountries = [
      // Afrique Francophone (Priorité)
      { code: 'SN', name: 'Sénégal 🇸🇳' },
      { code: 'CI', name: 'Côte d\'Ivoire 🇨🇮' },
      { code: 'CM', name: 'Cameroun 🇨🇲' },
      { code: 'BJ', name: 'Bénin 🇧🇯' },
      { code: 'BF', name: 'Burkina Faso 🇧🇫' },
      { code: 'CG', name: 'Congo-Brazzaville 🇨🇬' },
      { code: 'CD', name: 'RD Congo 🇨🇩' },
      { code: 'GA', name: 'Gabon 🇬🇦' },
      { code: 'GN', name: 'Guinée 🇬🇳' },
      { code: 'ML', name: 'Mali 🇲🇱' },
      { code: 'NE', name: 'Niger 🇳🇪' },
      { code: 'TD', name: 'Tchad 🇹🇩' },
      { code: 'TG', name: 'Togo 🇹🇬' },
      { code: 'MG', name: 'Madagascar 🇲🇬' },
      { code: 'BI', name: 'Burundi 🇧🇮' },
      { code: 'RW', name: 'Rwanda 🇷🇼' },
      { code: 'MA', name: 'Maroc 🇲🇦' },
      { code: 'DZ', name: 'Algérie 🇩🇿' },
      { code: 'TN', name: 'Tunisie 🇹🇳' },
      // Autres Pays Africains
      { code: 'NG', name: 'Nigeria 🇳🇬' },
      { code: 'GH', name: 'Ghana 🇬🇭' },
      { code: 'KE', name: 'Kenya 🇰🇪' },
      { code: 'ZA', name: 'Afrique du Sud 🇿🇦' },
      { code: 'EG', name: 'Égypte 🇪🇬' },
      { code: 'ET', name: 'Éthiopie 🇪🇹' },
      // Pays Majeurs (Hors Afrique)
      { code: 'FR', name: 'France 🇫🇷' },
      { code: 'CH', name: 'Suisse 🇨🇭' },
      { code: 'BE', name: 'Belgique 🇧🇪' },
      { code: 'CA', name: 'Canada 🇨🇦' },
      { code: 'US', name: 'États-Unis 🇺🇸' },
      { code: 'GB', name: 'Royaume-Uni 🇬🇧' }
    ];
    
    setCountries(africanCountries);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await projectsAPI.createProject(formData);
      
      // Invalider le cache des projets pour forcer le rechargement
      await queryClient.invalidateQueries('projects');
      await queryClient.invalidateQueries('my-projects');
      
      toast.success('Projet créé avec succès !');
      navigate('/projects');
    } catch (error) {
      console.error('Erreur création projet:', error);
      const fieldError = error.response?.data?.errors?.[0]?.message;
      toast.error(fieldError || error.response?.data?.message || 'Erreur lors de la création du projet');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-text mb-2">
              Soumettre un Projet
            </h1>
            <p className="text-gray-600">
              Partagez votre idée innovante et attirez des investisseurs
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Informations de base */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-text mb-4">
                Informations de Base
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-text mb-2">
                    Titre du Projet *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Nom de votre projet"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text mb-2">
                    Catégorie *
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Sélectionner une catégorie</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text mb-2">
                    Stade de Développement *
                  </label>
                  <select
                    name="stage"
                    value={formData.stage}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Sélectionner un stade</option>
                    {stages.map(stage => (
                      <option key={stage} value={stage}>{stage}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text mb-2">
                    Pays *
                  </label>
                  <select
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Sélectionner un pays</option>
                    {countries.map(country => (
                      <option key={country.code} value={country.code}>{country.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-text mb-2">
                  Description du Projet *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Décrivez votre projet en détail. Expliquez le problème que vous résolvez, votre solution, et pourquoi elle est unique..."
                />
                <AiWritingAssistant
                  formData={formData}
                  countryName={countries.find(c => c.code === formData.country)?.name}
                  onApply={updates => setFormData(prev => ({ ...prev, ...updates }))}
                />
              </div>
            </div>

            {/* Financement */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-text mb-4">
                Financement
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-text mb-2">
                    Montant Recherché *
                  </label>
                  <input
                    type="number"
                    name="fundingNeeded"
                    value={formData.fundingNeeded}
                    onChange={handleChange}
                    required
                    min="0"
                    step="1000"
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="50000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text mb-2">
                    Devise *
                  </label>
                  <select
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {currencies.map(currency => (
                      <option key={currency.code} value={currency.code}>
                        {currency.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text mb-2">
                    ROI Attendu (%)
                  </label>
                  <input
                    type="number"
                    name="expectedROI"
                    value={formData.expectedROI}
                    onChange={handleChange}
                    min="0"
                    max="1000"
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="25"
                  />
                </div>
              </div>
            </div>

            {/* Détails du projet */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-text mb-4">
                Détails du Projet
              </h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-text mb-2">
                    Modèle Économique
                  </label>
                  <textarea
                    name="businessModel"
                    value={formData.businessModel}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Comment votre projet génère-t-il des revenus ?"
                  />
                  <AiFieldAssistant
                    field="businessModel"
                    label="Modèle économique"
                    formData={formData}
                    countryName={countries.find(c => c.code === formData.country)?.name}
                    onApply={text => setFormData(prev => ({ ...prev, businessModel: text }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text mb-2">
                    Marché Cible
                  </label>
                  <textarea
                    name="targetMarket"
                    value={formData.targetMarket}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Qui sont vos clients cibles ?"
                  />
                  <AiFieldAssistant
                    field="targetMarket"
                    label="Marché cible"
                    formData={formData}
                    countryName={countries.find(c => c.code === formData.country)?.name}
                    onApply={text => setFormData(prev => ({ ...prev, targetMarket: text }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text mb-2">
                    Avantage Concurrentiel
                  </label>
                  <textarea
                    name="competitiveAdvantage"
                    value={formData.competitiveAdvantage}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Qu'est-ce qui rend votre projet unique ?"
                  />
                  <AiFieldAssistant
                    field="competitiveAdvantage"
                    label="Avantage concurrentiel"
                    formData={formData}
                    countryName={countries.find(c => c.code === formData.country)?.name}
                    onApply={text => setFormData(prev => ({ ...prev, competitiveAdvantage: text }))}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-text mb-2">
                      Taille de l'Équipe
                    </label>
                    <input
                      type="text"
                      name="teamSize"
                      value={formData.teamSize}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="ex: 3-5 personnes"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text mb-2">
                      Timeline
                    </label>
                    <input
                      type="text"
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="ex: 6-12 mois"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text mb-2">
                    Informations de Contact
                  </label>
                  <textarea
                    name="contactInfo"
                    value={formData.contactInfo}
                    onChange={handleChange}
                    rows={2}
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Téléphone, e-mail ou site pour vous joindre"
                  />
                  <p className="mt-1 text-xs text-gray-500">Visible par tous les visiteurs de la Bourse, même sans compte. N'écrivez que ce que vous acceptez de rendre public.</p>
                </div>
              </div>
            </div>

            {/* Boutons */}
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => navigate('/projects')}
                className="px-6 py-3 border border-border text-text rounded-lg hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Création...' : 'Créer le Projet'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateProject;